// Manually reconstructed from the user-supplied Bitesize Piano lesson:
// https://www.youtube.com/watch?v=OzTlZia4bOM
// See docs/LIBRARY-SOURCES.md for the visual/caption evidence and limitations.
// One continuous song arrangement. Musical repeats are written out below;
// lesson demonstrations, retries, spoken pauses and recap performances are omitted.
// The lesson's closing performance covers only post-chorus through the ending;
// its earlier recap covers intro through first chorus. No vocal melody is added.
import { validateMusic } from '../core/music.js';

export const SOMEWHERE_TEMPO = 86; // Chosen practice tempo, not the lesson clock.
const chord = (right, bass, hits = 8) => ({ right, bass, hits });
const a5 = chord('A3 E4 A4', 'A1 A2');
const movingA = [chord('A3 E4 A4', 'G#1 G#2', 3), chord('A3 D4 A4', 'G#1 G#2', 3), chord('A3 C#4 A4', 'G#1 G#2', 2)];
const movingMajor7 = [chord('A3 E4 G#4', 'G#1 G#2', 3), chord('A3 D4 A4', 'G#1 G#2', 3), chord('A3 C#4 G#4', 'G#1 G#2', 2)];
const introBm = [chord('A3 D4 A4', 'B1 B2', 4), chord('B3 D4 B4', 'B1 B2', 2), chord('A3 D4 A4', 'B1 B2', 2)];
const introE = [chord('A3 E4 A4', 'E2 E3', 3), chord('G#3 E4 G#4', 'E2 E3', 3), chord('A3 E4 A4', 'E2 E3', 2)];
const verse1 = [
  [chord('A3 C#4 E4', '')],
  [chord('G#3 C#4 E4', '', 3), chord('G#3 D4 E4', '', 3), chord('G#3 C#4 E4', '', 2)],
  [chord('B3 D4 F#4', '')],
  [chord('B3 E4 A4', '', 3), chord('B3 E4 G#4', '', 3), chord('B3 E4 A4', '', 2)],
];
const verse2 = [
  [a5], movingA, [chord('D4 A4 B4', 'B1 B2')],
  [chord('E4 A4 B4', 'E2 E3', 3), chord('E4 G#4 B4', 'E2 E3', 3), chord('E4 A4 B4', 'E2 E3', 2)],
];
const preEnd = [
  [chord('D4 F#4 A4', 'D2 D3')],
  [chord('D4 F#4 A4 B4', 'D2 D3', 4), chord('D4 E4 A4 B4', 'E2 E3', 2), chord('D4 E4 G#4 B4', 'E2 E3', 2)],
];
const pre1 = [
  [chord('C#4 F#4 A4', 'F#1 F#2')],
  [chord('C#4 E4 G#4', 'C#2 C#3', 3), chord('C#4 E4 A4', 'C#2 C#3', 3), chord('C#4 E4 G#4', 'C#2 C#3', 2)], ...preEnd,
];
const pre2 = [
  [chord('C#4 F#4 C#5', 'F#1 F#2')],
  [chord('C#4 G#4 C#5', 'C#2 C#3', 3), chord('C#4 A4 C#5', 'C#2 C#3', 3), chord('C#4 G#4 C#5', 'C#2 C#3', 2)], ...preEnd,
];
const chorusPair = [
  [chord('D4 A4 D5', 'B1 B2')],
  [chord('E4 A4 B4 E5', 'C#2 C#3', 4), chord('E4 A4 B4 E5', 'E2 E3', 4)],
];
const d6 = chord('F#4 A4 B4 D5', 'D2 D3');
const e6 = chord('E4 G#4 B4 C#5', 'E2 E3');
const chorusEnd = [chord('E4 G#4 C#5 E5', 'E2 E3', 4), chord('E4 B4 C#5 E5', 'A1 A2', 1), chord('E4 A4 C#5 E5', 'A1 A2', 3)];
const repeat = (bars, times) => Array.from({ length: times }, () => bars).flat();
const sections = [
  ['Intro', [[a5], movingA, introBm, introE, [a5], movingMajor7, introBm, introE]],
  ['Verse 1', repeat(verse1, 2)],
  ['Pre-chorus 1', repeat(pre1, 2)],
  ['Verse 2', repeat(verse2, 2)],
  ['Pre-chorus 2', repeat(pre2, 2)],
  ['Chorus 1', [...repeat(chorusPair, 3), [d6], chorusEnd]],
  ['Post-chorus', [[d6], [e6], [d6], [
    { ...e6, hits: 4 }, chord('D4 F#4 B4', 'E2 E3', 1), chord('E4 G#4 C#5', 'E2 E3', 1),
    chord('D4 F#4 B4', 'E2 E3', 1), chord('D4 F#4 B4', 'F2 F3', 1),
  ]]],
  ['Pre-chorus 3', repeat(pre2, 2)],
  ['Chorus 2', [...repeat(chorusPair, 3), [d6], [{ ...d6, hits: 4 }, chord('E4 G#4 C#5 E5', 'E2 E3', 4)]]],
  ['Final chorus', [...repeat(chorusPair, 3), [d6], chorusEnd]],
  ['Outro', [[d6], [{ ...e6, hits: 6 }, chord('Eb4 G4 Bb4 C5', 'Eb2 Eb3', 2)], [d6], [
    { ...e6, hits: 4 }, { ...d6, hits: 2 }, chord('F#4 A4 D5', 'D2 D3', 2),
  ], [{ ...chord('A3 C#4 E4', 'A2 A3'), sustain: true }]]],
];

function pitchNote(name) {
  const [, step, accidental, octave] = /^([A-G])([#b]?)(\d)$/.exec(name);
  const alter = accidental === '#' ? 1 : accidental === 'b' ? -1 : 0;
  return { pitch: (Number(octave) + 1) * 12 + { C:0,D:2,E:4,F:5,G:7,A:9,B:11 }[step] + alter, step, alter, octave: Number(octave) };
}

export function somewhereAccompaniment() {
  const notes = [], measures = [], markers = [], secondsPerBeat = 60 / SOMEWHERE_TEMPO;
  let bar = 0;
  const add = (names, beat, durationBeats, track) => {
    for (const name of names.split(' ').filter(Boolean)) notes.push({
      id: `note-${notes.length + 1}`, ...pitchNote(name), start: beat * secondsPerBeat,
      duration: durationBeats * secondsPerBeat, durationBeats, track, staff: track + 1,
      measure: bar + 1, beat: beat % 4 + 1, velocity: track === 1 ? .64 : .76,
      reviewed: false, confidence: .7,
    });
  };
  for (const [title, bars] of sections) {
    markers.push({ title, start: bar * 4 * secondsPerBeat, measure: bar + 1 });
    for (const groups of bars) {
      if (groups.reduce((sum, group) => sum + group.hits, 0) !== 8) throw new Error('Accompaniment bar must contain eight eighth-note pulses.');
      measures.push({ number: bar + 1, start: bar * 4 * secondsPerBeat, duration: 4 * secondsPerBeat, numerator: 4, denominator: 4 });
      let pulse = 0;
      for (let index = 0; index < groups.length; index++) {
        const group = groups[index], beat = bar * 4 + pulse / 2;
        // Sustain each bass through inner RH changes; rearticulate at a new bar.
        if (index === 0 || group.bass !== groups[index - 1].bass) {
          let hits = group.hits;
          for (let next = index + 1; next < groups.length && groups[next].bass === group.bass; next++) hits += groups[next].hits;
          // Avoid two voices triggering the same A3 in the final held chord.
          add(group.bass.split(' ').filter(name => !group.sustain || !group.right.split(' ').includes(name)).join(' '), beat, hits / 2, 1);
        }
        if (group.sustain) add(group.right, beat, group.hits / 2, 0);
        else for (let hit = 0; hit < group.hits; hit++) add(group.right, beat + hit / 2, .5, 0);
        pulse += group.hits;
      }
      bar++;
    }
  }
  const music = validateMusic({ version: 1, title: 'Somewhere Only We Know · full-song piano accompaniment',
    source: { kind: 'visual', name: 'somewhere-only-we-know.accompaniment.json', reviewed: false, confidence: .7,
      method: 'Manual reconstruction of Bitesize Piano keyboard demonstrations and spoken note instructions; quantized practice arrangement' },
    notes, measures, duration: bar * 4 * secondsPerBeat,
    tracks: [{ id: 0, name: 'Right hand · accompaniment' }, { id: 1, name: 'Left hand · bass octaves' }],
    tempos: [{ time: 0, bpm: SOMEWHERE_TEMPO }], anchors: [], views: { falling: true, keyboard: true, chords: true, sheet: true },
    warnings: ['Complete song-form piano accompaniment; no sung melody. Teaching repeats, retries and spoken pauses are omitted.',
      'Steady 86 BPM practice arrangement. Bass sustain, articulations and transition rhythms are simplified; no video synchronization.',
      'Review notes before treating this as an exact score. Confidence is an editorial estimate, not a measured probability.'],
    extraction: { method: 'manual-lesson-reconstruction', video: 'https://www.youtube.com/watch?v=OzTlZia4bOM', sections: markers },
  });
  return { music, markers };
}
